import React, { memo, useState } from "react";
import { Form, Divider, Row, Rate } from "antd";
import FormBuilder from "antd-form-builder";
import moment from "moment";
import {
	IFormProps,
	ILayoutProps,
	ICustomFormItemProps,
	EWidgets
} from "./interface";
import ZInput from "@molecules/ZInput";
import ZInputNumber from "@atoms/ZInputNumber";
import ZSelect from "@atoms/ZSelect";
import ZCheckboxGroup from "@atoms/ZCheckboxGroup";
import ZRadioGroup from "@atoms/ZRadioGroup";
import ZButton from "@atoms/ZButton";
import ZMessage from "@molecules/ZMessage";
import ZCheckbox from "@atoms/ZCheckbox";
import DatePicker from "@molecules/DatePicker";
import ZTimePicker from "@molecules/ZTimePicker";
import "./index.less";
import { ZSectionTitle } from "@molecules/ZSection";
import Col from "antd/es/grid/col";
import { CurrencyOptions, CustomField, CustomFieldType } from "@/Contracts";

const defaultFormLayout: ILayoutProps = {
	labelCol: { span: 4 },
	wrapperCol: { span: 20 }
};

// CUSTOM WIDGETS
const ZInputWidget = ({ value, onChange, ...rest }: ICustomFormItemProps) => (
	<ZInput {...rest} value={value} onChange={value => onChange(value)} />
);

FormBuilder.defineWidget(EWidgets.INPUT, ZInputWidget);

const ZInpuPasswordWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<ZInput
		inputType="password"
		{...rest}
		value={value}
		onChange={value => onChange(value)}
	/>
);

FormBuilder.defineWidget(EWidgets.INPUTPASSWORD, ZInpuPasswordWidget);

const ZInpuSearchWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<ZInput
		inputType="search"
		{...rest}
		value={value}
		onChange={value => onChange(value)}
	/>
);

FormBuilder.defineWidget(EWidgets.INPUTSEARCH, ZInpuSearchWidget);

const ZInputNumberWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<ZInputNumber {...rest} value={value} onChange={value => onChange(value)} />
);

FormBuilder.defineWidget(EWidgets.INPUTNUMBER, ZInputNumberWidget);

const ZInputNumberWithSelectWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<Row>
		<ZInputNumber
			value={value && value.number}
			onChange={v => onChange({ ...value, number: v })}
			className="input-number-yes-addon"
			{...rest}
		/>
		<ZSelect
			isInputNumberSelect={true}
			value={value && value.selection}
			onChange={v => onChange({ ...value, selection: v })}
			className="ml-4"
			{...rest}
		/>
	</Row>
);

FormBuilder.defineWidget(
	EWidgets.INPUTNUMBERWITHSELECT,
	ZInputNumberWithSelectWidget
);

const ZSelectWidget = ({ value, onChange, ...rest }: ICustomFormItemProps) => (
	<ZSelect {...rest} value={value} onChange={value => onChange(value)} />
);

FormBuilder.defineWidget(EWidgets.SELECT, ZSelectWidget);

const ZRateWidget = ({ value, onChange, ...rest }: ICustomFormItemProps) => (
	<Rate {...rest} value={value} onChange={value => onChange(value)} />
);

FormBuilder.defineWidget(EWidgets.RATE, ZRateWidget);

const ZCheckboxWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<ZCheckbox
		{...rest}
		defaultChecked={value}
		onChange={event => onChange(event.target.checked)}
	/>
);

FormBuilder.defineWidget(EWidgets.CHECKBOX, ZCheckboxWidget);

const ZCheckboxGroupWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<ZCheckboxGroup
		{...rest}
		value={value}
		onChange={value => onChange(value)}
	/>
);

FormBuilder.defineWidget(EWidgets.CHECKBOXGROUP, ZCheckboxGroupWidget);

const ZRadioGroupWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<ZRadioGroup
		{...rest}
		className={`whitespace-normal testing ${rest.className}`}
		value={value}
		onChange={value => onChange(value)}
	/>
);

FormBuilder.defineWidget(EWidgets.RADIOGROUP, ZRadioGroupWidget);

const ZDatePickerWidget = ({
	value,
	onChange,
	datePattern,
	...rest
}: ICustomFormItemProps) => {
	return (
		<DatePicker
			format={datePattern}
			{...rest}
			value={value}
			onChange={value => onChange(value)}
		/>
	);
};

FormBuilder.defineWidget(EWidgets.DATEPICKER, ZDatePickerWidget);

const ZTimePickerWidget = ({
	value,
	onChange,
	...rest
}: ICustomFormItemProps) => (
	<ZTimePicker {...rest} value={value} onChange={value => onChange(value)} />
);

FormBuilder.defineWidget(EWidgets.TIMEPICKER, ZTimePickerWidget);

export const getWidget = (fieldType: CustomFieldType) => {
	switch (fieldType) {
		case CustomFieldType.TextField:
			return EWidgets.INPUT;
		case CustomFieldType.NumberField:
			return EWidgets.INPUTNUMBER;
		case CustomFieldType.DecimalField:
			return EWidgets.INPUTNUMBER;
		case CustomFieldType.AmountField:
			return EWidgets.INPUTNUMBER;
		case CustomFieldType.Dropdown:
			return EWidgets.SELECT;
		case CustomFieldType.DateField:
			return EWidgets.DATEPICKER;
		case CustomFieldType.CurrencyField:
			return EWidgets.SELECT;
		default:
			return EWidgets.INPUT;
	}
};

export const getDropDownValue = (customField: CustomField) => {
	if (customField.field_type === CustomFieldType.Dropdown) {
		return customField.allowed_dropdown_values ?? [];
	} else if (customField.field_type === CustomFieldType.CurrencyField) {
		return CurrencyOptions.map(a => ({
			name: a.label,
			value: a.symbol
		}));
	}
	return [];
};

export const getPlaceholderText = (
	fieldType: CustomFieldType,
	fieldLabel: string
) => {
	if (fieldType == CustomFieldType.DateField) return "Select Date";
	else return `Please enter ${fieldLabel}`;
};

export const preventDecimal = (
	event: React.KeyboardEvent<HTMLInputElement>
) => {
	if (event.key === "." || event.key === "e") {
		event.preventDefault();
	}
};

export const getCustomFieldProps = (customField: CustomField) => {
	return {
		width: "full",
		selectOptions: getDropDownValue(customField),

		placeholder: getPlaceholderText(
			customField.field_type,
			customField.display_name
		),
		onKeyDown: (e: any) => {
			if (customField.field_type == CustomFieldType.NumberField)
				preventDecimal(e);
		}
	};
};

const ZForm: React.FC<IFormProps> = ({
	key = "",
	submitOnlyTouched = false,
	viewMode = false,
	disabled = false,
	loading = false,
	submitButtonText = "Submit",
	cancelButtonText = "Cancel",
	isStickFooter = false,
	isRightFooter = false,
	layout = "horizontal",
	isSubmitAllowed = true,
	isSubmitDisabled = false,
	isSubmitButtonDanger = false,
	requiredMark = true,
	isCancelAllowed = false,
	isDynamic = false,
	formLayout = defaultFormLayout,
	preserve,
	formFooter = null,
	shouldDisableBeforeTouch = true,
	className = "",
	form,
	sections = [],
	children,
	shouldResetFormFields = false,
	onFinish,
	submitButtonProps = {},
	initialValues,
	onValuesChange,
	shouldResetFormOnCancel = true,
	cancelButtonProps,
	sectionColumns = 1,
	...rest
}) => {
	const [isFieldTouched, setIsFieldTouched] = useState(false);
	const forceUpdate = FormBuilder.useForceUpdate();

	const handleChange = React.useCallback(
		(changedValues, values) => {
			setIsFieldTouched(true);
			isDynamic && forceUpdate();
			onValuesChange && onValuesChange(changedValues, values);
		},
		[forceUpdate, isDynamic, onValuesChange]
	);

	const handleFinish = React.useCallback(() => {
		form.validateFields()
			.then(
				async (
					values: Record<
						string,
						string | number | boolean | null | undefined
					>
				) => {
					let touchedValues: Record<
						string,
						string | number | boolean | null | undefined
					> = {};
					if (submitOnlyTouched) {
						for (const element in values) {
							if (form.isFieldTouched(element)) {
								touchedValues[element] = values[element];
							}
						}
					} else {
						touchedValues = values;
					}
					if (onFinish) {
						await onFinish(touchedValues);
						shouldResetFormFields && form.resetFields();
					}
				}
			)
			.catch(error => {
				console.error(error);
				ZMessage.error("Please resolve errors in your form");
			});
	}, [form, submitOnlyTouched, onFinish, shouldResetFormFields]);

	const handleCancel = () => {
		shouldResetFormOnCancel && form.resetFields();
		rest.onCancel && rest.onCancel();
	};

	const extraFooterSection =
		sections.filter(section => section.isFooterSection) &&
		sections.filter(section => section.isFooterSection)[0];

	return (
		<Form
			name={key}
			form={form}
			layout={layout}
			onValuesChange={handleChange}
			initialValues={initialValues}
			colon={false}
			labelAlign="left"
			scrollToFirstError
			requiredMark={requiredMark}
			preserve={preserve}
			{...formLayout}
			className={`${
				isStickFooter ? "z-form" : "z-form z-form-scroll"
			} ${className}`}
			onFinish={handleFinish}
			{...rest}
		>
			{sectionColumns > 1 ? (
				<Row>
					{sections.map(
						(section, index) =>
							(section.visible === undefined ||
								section.visible) &&
							!section.isFooterSection && (
								<Col
									span={24 / sectionColumns}
									key={`form_fragment_${index}`}
								>
									{!(typeof section.title === "object") ? (
										<ZSectionTitle
											heading={section.title}
											icon={section.icon}
											isTwoToneIcon={
												section.isTwoToneIcon
											}
											iconPlaceholder={
												section.iconPlaceholder
											}
											subHeading={section.subtitle}
											shouldHideSectionPadding={
												section.shouldHideSectionBottomPadding
											}
											showSubheadingTooltip={
												section.showSubheadingTooltip
											}
										/>
									) : (
										section.title
									)}
									{section.title && !section.subtitle ? (
										<div className="pb-md" />
									) : section.subtitle ? (
										<div className="pb-sm" />
									) : null}
									<div
										className={
											section.isIndentFormItems
												? "pl-10"
												: ""
										}
									>
										<FormBuilder
											meta={{
												formItemLayout: formLayout,
												...section.meta
											}}
											getMeta={section.getMeta}
											form={form}
											viewMode={viewMode}
											disabled={disabled || loading}
										/>
									</div>
								</Col>
							)
					)}
				</Row>
			) : (
				<div className={isStickFooter ? "form-container" : ""}>
					{sections.map(
						(section, index) =>
							(section.visible === undefined ||
								section.visible) &&
							!section.isFooterSection && (
								<React.Fragment key={`form_fragment_${index}`}>
									<React.Fragment>
										{index !== 0 &&
											!section.shouldHideSectionTopPadding && (
												<div className="pt-lg" />
											)}
										{!(
											typeof section.title === "object"
										) ? (
											<ZSectionTitle
												heading={section.title}
												icon={section.icon}
												isTwoToneIcon={
													section.isTwoToneIcon
												}
												iconPlaceholder={
													section.iconPlaceholder
												}
												subHeading={section.subtitle}
												shouldHideSectionPadding={
													section.shouldHideSectionBottomPadding
												}
												showSubheadingTooltip={
													section.showSubheadingTooltip
												}
											/>
										) : (
											section.title
										)}
										{section.hidePaddingAfterTitle ===
											undefined ||
										!section.hidePaddingAfterTitle ? (
											section.title &&
											!section.subtitle ? (
												<div className="pb-lg" />
											) : section.subtitle ? (
												<div className="pb-md" />
											) : null
										) : null}

										<div
											className={
												section.isIndentFormItems
													? "pl-10"
													: ""
											}
										>
											<FormBuilder
												meta={{
													formItemLayout: formLayout,
													...section.meta
												}}
												getMeta={section.getMeta}
												form={form}
												viewMode={viewMode}
												disabled={disabled || loading}
											/>
										</div>
									</React.Fragment>
									{index !==
										(sections.length as number) - 1 &&
										!section.hideDivider && <Divider />}
								</React.Fragment>
							)
					)}
				</div>
			)}
			{formFooter && <div className="py-3">{formFooter}</div>}
			{!viewMode && isSubmitAllowed && !formFooter && (
				<React.Fragment>
					{isStickFooter && <Divider />}
					<Form.Item
						className={`${
							isStickFooter ? "pt-4 mb-0" : "mb-0"
						} form-footer`}
						wrapperCol={{ span: 24 }}
					>
						{extraFooterSection && (
							<div className="inline-block w-2/4">
								<FormBuilder
									meta={{
										formItemLayout: formLayout,
										...extraFooterSection.meta
									}}
									getMeta={extraFooterSection.getMeta}
									form={form}
									viewMode={viewMode}
									disabled={disabled || loading}
								/>
							</div>
						)}

						<div
							className={`inline-flex items-center ${
								extraFooterSection ? "w-2/4" : "w-full"
							} ${
								isRightFooter ? "justify-end" : "justify-start"
							}`}
						>
							{isCancelAllowed && (
								<ZButton
									disabled={disabled || loading}
									loading={loading}
									onClick={handleCancel}
									{...cancelButtonProps}
									className={`mr-4 ${cancelButtonProps?.className}`}
								>
									{cancelButtonText}
								</ZButton>
							)}
							<ZButton
								type="primary"
								danger={isSubmitButtonDanger}
								ghost={isSubmitButtonDanger}
								disabled={
									disabled ||
									(!isFieldTouched &&
										shouldDisableBeforeTouch) ||
									loading ||
									isSubmitDisabled
								}
								loading={loading}
								htmlType="submit"
								data-testid="submit"
								{...submitButtonProps}
							>
								{submitButtonText}
							</ZButton>
						</div>
					</Form.Item>
				</React.Fragment>
			)}
			<div>{children}</div>
		</Form>
	);
};

export default memo(ZForm);
